import {
  createContext,
  use,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import {
  applyTheme,
  DEFAULT_THEME,
  isHexColor,
  readStoredTheme,
  writeStoredTheme,
  type AccentId,
  type Theme,
  type ThemeMode,
} from "./theme.js";

interface ThemeContextValue extends Theme {
  setMode: (mode: ThemeMode) => void;
  toggleMode: () => void;
  setAccent: (accent: AccentId) => void;
  setCustomAccent: (hex: string) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children?: ReactNode }) {
  const [theme, setTheme] = useState<Theme>(() =>
    typeof window === "undefined" ? DEFAULT_THEME : readStoredTheme(),
  );

  // Before paint, and again on every change: the same values the pre-paint script
  // in index.html already applied, so mounting never moves the theme.
  useLayoutEffect(() => {
    applyTheme(document.documentElement, theme);
    writeStoredTheme(theme);
  }, [theme]);

  const value = useMemo<ThemeContextValue>(
    () => ({
      ...theme,
      setMode: (mode) => {
        setTheme((current) => ({ ...current, mode }));
      },
      toggleMode: () => {
        setTheme((current) => ({
          ...current,
          mode: current.mode === "dark" ? "light" : "dark",
        }));
      },
      setAccent: (accent) => {
        setTheme((current) => ({ ...current, accent }));
      },
      setCustomAccent: (hex) => {
        if (!isHexColor(hex)) return;
        setTheme((current) => ({
          ...current,
          accent: "custom",
          customAccent: hex.toLowerCase(),
        }));
      },
    }),
    [theme],
  );

  return <ThemeContext value={value}>{children}</ThemeContext>;
}

export function useTheme(): ThemeContextValue {
  const value = use(ThemeContext);
  if (!value) throw new Error("useTheme must be used inside ThemeProvider");
  return value;
}
